import { z } from 'zod';
import { refersTo } from '../common/refers-to';
import { ContactEmailAddressSchema } from '../relationship/contacts.schemas';
import { DOCUMENT_TEMPLATE_CATEGORIES } from '../identity/document-templates.schemas';

// The same enum as `GeneratedDocumentTypeSchema`, built from its source: `deal-documents.schemas` imports this file.
const DocumentTypeSchema = z.enum(DOCUMENT_TEMPLATE_CATEGORIES);

export const SIGNING_PAGE_PATH = '/sign';

export const SIGNING_REQUEST_STATUSES = ['SENT', 'PARTIALLY_SIGNED', 'SIGNED', 'WITHDRAWN'] as const;
export const SigningRequestStatusSchema = z.enum(SIGNING_REQUEST_STATUSES);
export type SigningRequestStatus = z.infer<typeof SigningRequestStatusSchema>;

export const SigningDraftSchema = z.object({
  documentType: DocumentTypeSchema,
  templateName: z.string(),
  createdAt: z.iso.datetime(),
  owners: z
    .array(z.object({ contactId: refersTo('contact', z.uuid()), name: z.string(), email: z.string().nullable() }))
    .min(1), // Deal owner order
  agentName: z.string(), // „{Vorname} {Nachname}“ from the member's profile; '' when the profile has neither
  companyName: z.string(), // '' when not set
  blockReason: z.enum(['COMPANY_DATA_INCOMPLETE']).nullable(),
});
export type SigningDraft = z.infer<typeof SigningDraftSchema>;

export const SendForSignatureBodySchema = z.object({
  requestKey: z.uuid(), // generated when the dialog opens; a replay returns the first request
  ownerContactIds: z.array(z.uuid()).min(1).max(20), // the owners the dialog showed, in its order
  ownerEmails: z
    .array(z.object({ contactId: z.uuid(), email: ContactEmailAddressSchema })) // the Kontakt form's address rule
    .max(20)
    .default([]),
  agentSignatureName: z.string().trim().min(1).max(120),
  signsForCompany: z.literal(true),
});
export type SendForSignatureBody = z.infer<typeof SendForSignatureBodySchema>;

export const SIGNER_EMAIL_STATES = ['SENT', 'DELIVERED', 'NOT_DELIVERED'] as const;
export type SignerEmailState = (typeof SIGNER_EMAIL_STATES)[number];

export const SigningSignerSummarySchema = z.object({
  signerId: z.uuid(),
  contactId: refersTo('contact', z.uuid()),
  name: z.string(),
  email: z.string(),
  signedAt: z.iso.datetime().nullable(),
  invitedAt: z.iso.datetime().nullable(), // the INVITATION's sentAt; null while it never went out
  lastEmailState: z.enum(SIGNER_EMAIL_STATES).nullable(), // derived from the newest owner e-mail to this signer
  earlyStartRequested: z.boolean().nullable(), // null for a Vollmacht or before signing
  revokedAt: z.iso.datetime().nullable(), // the Eigentümer's Widerruf of a signed Maklervertrag
  acceptanceReplyMarkedAt: z.iso.datetime().nullable(), // null while outstanding, before signing and for a Vollmacht
});

export const SigningSummarySchema = z.object({
  id: z.uuid(),
  status: SigningRequestStatusSchema,
  sentAt: z.iso.datetime(),
  sentByMemberId: refersTo('member', z.uuid()),
  signedCount: z.number().int().min(0),
  signerCount: z.number().int().min(1),
  signers: z.array(SigningSignerSummarySchema).min(1),
  completedAt: z.iso.datetime().nullable(),
  signedFileReady: z.boolean(),
  lastReminderAt: z.iso.datetime().nullable(),
  automaticReminderOn: z.iso.date().nullable(), // Berlin date of sentAt + 72 h while the request is open and that moment is still ahead
  withdrawnAt: z.iso.datetime().nullable(),
});
export type SigningSummary = z.infer<typeof SigningSummarySchema>;

export const SendForSignatureResponseSchema = SigningSummarySchema;
export type SendForSignatureResponse = z.infer<typeof SendForSignatureResponseSchema>;

export const RemindSignersBodySchema = z.object({}).strict();
export const WithdrawSigningRequestBodySchema = z.object({}).strict();
export const RemindSignersResponseSchema = SigningSummarySchema;
export const WithdrawSigningRequestResponseSchema = SigningSummarySchema;

export const MarkSigningAcceptanceReplyBodySchema = z.object({}).strict();
export const MarkSigningAcceptanceReplyResponseSchema = SigningSummarySchema;
